import React from "react";
import { motion } from "framer-motion";
import { Quote, Star } from "lucide-react";
import { testimonialsData } from "../../data/siteData";

export const TestimonialsSection = () => {
  // Double the testimonials for seamless infinite scroll
  const duplicatedTestimonials = [...testimonialsData, ...testimonialsData];

  return (
    <section
      id="testimonials"
      className="py-20 sm:py-32 bg-warm-cream/30 overflow-hidden relative z-20"
      data-testid="testimonials-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        {/* Header */}
        <div className="text-center">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-warm-sage uppercase tracking-[0.2em] text-xs font-bold mb-4"
          >
            Client Stories
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="font-serif text-3xl sm:text-4xl lg:text-5xl text-warm-brown mb-4"
          >
            Words From Those Who Healed
          </motion.h2>
        </div>
      </div>

      {/* Infinite Scroll Carousel */}
      <div className="relative">
        {/* Gradient overlays for seamless effect */}
        <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-warm-cream/30 to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-warm-cream/30 to-transparent z-10 pointer-events-none" />

        {/* Scrolling container */}
        <motion.div
          className="flex gap-6 py-4"
          animate={{ x: ["0%", "-50%"] }}
          transition={{
            x: {
              duration: 30,
              repeat: Infinity,
              ease: "linear"
            }
          }}
          style={{ width: "fit-content" }}
        >
          {duplicatedTestimonials.map((testimonial, index) => (
            <motion.div
              key={`${testimonial.id}-${index}`}
              whileHover={{ scale: 1.02, y: -5 }}
              className="flex-shrink-0 w-80 sm:w-96 bg-warm-card rounded-3xl p-6 sm:p-8 border border-warm-rose/10 shadow-lg hover:shadow-xl transition-all duration-300"
              data-testid={`testimonial-card-${index}`}
            >
              {/* Quote Icon */}
              <div className="mb-4">
                <Quote className="w-8 h-8 text-warm-rose/40" />
              </div>

              {/* Quote Text */}
              <p className="font-sans text-warm-brown/80 leading-relaxed mb-6 text-sm sm:text-base italic">
                "{testimonial.quote}"
              </p>

              {/* Stars */}
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className="w-4 h-4 fill-warm-sage text-warm-sage"
                  />
                ))}
              </div>

              {/* Author */}
              <div className="flex items-center gap-3">
                {/* Avatar */}
                <div className="w-12 h-12 rounded-full bg-warm-rose/20 flex items-center justify-center">
                  <span className="font-serif text-warm-brown font-semibold">
                    {testimonial.initials}
                  </span>
                </div>
                <div>
                  <p className="font-semibold text-warm-brown text-sm">
                    {testimonial.name}
                  </p>
                  <p className="text-warm-brown/60 text-xs">
                    {testimonial.location}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
